import {useEffect, useMemo} from 'react';
import type {GraphQLTaggedNode} from 'relay-runtime';
import type {FieldDescriptor, FieldOption} from './actionCardTypes';
import {useForecastFetchQuery} from '../../forecast-app/shared/hooks/useForecastFetchQuery';

/**
 * Minimal no-op query placeholder used when no optionsSource is active.
 * useForecastFetchQuery requires a GraphQLTaggedNode at call-time; passing this
 * prevents React hook rule violations (hooks can't be called conditionally).
 * The fetch() is never invoked when source is absent, so this node is never
 * dispatched to the Relay network layer.
 *
 * NOTE: We construct a minimal ConcreteRequest object by hand rather than via
 * graphql`...` because this file has no relay-compiler artefact to generate —
 * the query is intentionally never sent. The cast to GraphQLTaggedNode is
 * safe; relay-runtime only inspects the object when fetch() is called.
 */
const NoOpQuery = {
	kind: 'Request',
	params: {
		cacheID: 'useFieldOptionsNoOp',
		id: null,
		metadata: {},
		name: 'useFieldOptionsNoOpQuery',
		operationKind: 'query',
		text: 'query useFieldOptionsNoOpQuery { viewer { actualPersonId } }',
	},
	fragment: {
		kind: 'Fragment',
		name: 'useFieldOptionsNoOpQuery',
		type: 'Query',
		metadata: null,
		argumentDefinitions: [],
		selections: [],
	},
} as unknown as GraphQLTaggedNode;

/**
 * Registry of known optionsSource keys.
 *
 * DONE_WITH_CONCERNS — all three sources currently return [] because no clean
 * standalone Relay query exists in web-front that can be called imperatively
 * through useForecastFetchQuery for project_persons, roles, or workflow_columns.
 * Existing data for these entities is fetched via fragment containers tied to
 * page-level ForecastQueryRenderers, not via standalone imperative queries.
 * Wiring real queries requires design approval to create new top-level queries;
 * that work is out of scope for this task.
 *
 * When a suitable query is available, replace the no-op registry entry with:
 *   {query: TheRealQuery, map: (data) => data?.viewer?.... .map(...)}
 */
type SourceEntry = {
	query: GraphQLTaggedNode;
	map: (data: unknown) => FieldOption[];
};

const NOOP_ENTRY: SourceEntry = {
	query: NoOpQuery,
	map: () => [],
};

const SOURCE_REGISTRY: Record<string, SourceEntry> = {
	// project_persons: no clean standalone query available yet → returns []
	project_persons: NOOP_ENTRY,
	// roles: no clean standalone query available yet → returns []
	roles: NOOP_ENTRY,
	// workflow_columns: no clean standalone query available yet → returns []
	workflow_columns: NOOP_ENTRY,
};

/**
 * Pure mapper from a known optionsSource key + raw query data → FieldOption[].
 * Exported for unit-testability without the hook.
 */
export const mapSourceData = (source: string, data: unknown): FieldOption[] => {
	const entry = SOURCE_REGISTRY[source];
	if (!entry) return [];
	return entry.map(data);
};

/**
 * useFieldOptions — resolves a FieldDescriptor's option list.
 *
 * Behaviour:
 *  - descriptor.options present  → return them, loading: false, NO fetch
 *  - descriptor.optionsSource present → resolve via SOURCE_REGISTRY (best-effort;
 *    unknown sources return [])
 *  - neither present              → {options: [], loading: false}
 *
 * Per AGENTS.md Relay rule: imperative dropdown data goes through
 * useForecastFetchQuery, never raw Relay / useLazyLoadQuery.
 */
export const useFieldOptions = (descriptor: FieldDescriptor): {options: FieldOption[]; loading: boolean} => {
	const inline = descriptor.options;
	const source = inline ? undefined : descriptor.optionsSource;

	const entry = source ? SOURCE_REGISTRY[source] : undefined;
	const queryToUse = entry?.query ?? NoOpQuery;

	const {data, fetch, loading} = useForecastFetchQuery(queryToUse);

	// Trigger a fetch only when there is a real (non-NOOP) source entry.
	// NOOP entries are never fetched — the hook must remain inert for them.
	useEffect(() => {
		if (source && entry && entry !== NOOP_ENTRY) {
			fetch();
		}
	}, [source, entry, fetch]);

	return useMemo(() => {
		if (inline) return {options: inline, loading: false};
		if (!source) return {options: [], loading: false};
		if (!entry) return {options: [], loading: false};
		return {options: mapSourceData(source, data), loading};
	}, [inline, source, entry, data, loading]);
};
